import React, { useState, useId } from "react";
import Icon from "../components/Icon";
import Reveal from "../components/Reveal";
import HeroBackdrop from "../components/HeroBackdrop";
import Button from "../components/Button";
import SectionHeading from "../components/SectionHeading";

/* ------------------------------------------------------------------ */
/*  Contact details                                                     */
/* ------------------------------------------------------------------ */

const EMAIL = "contact@mondoway.com";
const PHONE_DISPLAY = "+962 123 456 789";
const PHONE_DIAL = "+962123456789";
const ADDRESS_LINES = ["123 Innovation Street", "Tech District, Amman", "Jordan 11183"];
const MAPS_URL = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
  ADDRESS_LINES.join(", ")
)}`;

const CONTACT_METHODS = [
  {
    id: 1,
    icon: "mail",
    title: "Email Us",
    subtitle: "Send us an email",
    value: EMAIL,
    action: `mailto:${EMAIL}`,
    description: "Get in touch via email for detailed inquiries"
  },
  {
    id: 2,
    icon: "phone",
    title: "Call Us",
    subtitle: "Speak directly with us",
    value: PHONE_DISPLAY,
    action: `tel:${PHONE_DIAL}`,
    description: "Call us for immediate assistance"
  },
  {
    id: 3,
    icon: "chat",
    title: "WhatsApp",
    subtitle: "Chat with us instantly",
    value: PHONE_DISPLAY,
    action: `https://wa.me/${PHONE_DIAL.replace("+", "")}`,
    description: "Quick responses via WhatsApp"
  },
  {
    id: 4,
    icon: "pin",
    title: "Visit Us",
    subtitle: "Our office location",
    value: "Amman, Jordan",
    action: MAPS_URL,
    description: "Schedule a meeting at our office"
  }
];

const OFFICE_DETAILS = [
  { icon: "pin", title: "Address", lines: ADDRESS_LINES },
  {
    icon: "clock",
    title: "Business Hours",
    lines: [
      "Monday – Friday: 9:00 AM – 6:00 PM",
      "Saturday: 10:00 AM – 2:00 PM",
      "Sunday: Closed"
    ]
  },
  { icon: "cars", title: "Parking", lines: ["Free parking available", "Accessible entrance"] }
];

const SOCIAL_LINKS = [
  { icon: "facebook", name: "Facebook", url: "#" },
  { icon: "instagram", name: "Instagram", url: "#" },
  { icon: "x", name: "X", url: "#" },
  { icon: "linkedin", name: "LinkedIn", url: "#" },
  { icon: "youtube", name: "YouTube", url: "#" }
];

/** Mondoway's own service categories — what people actually write in about. */
const SERVICE_OPTIONS = [
  "Restaurants",
  "Clothes",
  "Gifts",
  "Electronics",
  "Home",
  "Cars",
  "Real Estate",
  "Health Care",
  "Factories",
  "Mart Market",
  "Tourism",
  "Bazaar",
  "Cinema",
  "Listing my business",
  "Other"
];

const FAQS = [
  {
    question: "Is Mondoway free to download?",
    answer:
      "Yes. Mondoway is free to download on Google Play, the App Store and Huawei AppGallery, and creating an account costs nothing."
  },
  {
    question: "What can I do inside the app?",
    answer:
      "Mondoway brings thirteen categories into one place — restaurants, clothes, gifts, electronics, home, cars, real estate, health care, factories, Mart Market, tourism, Bazaar and cinema — so you can shop, book and sell without switching apps."
  },
  {
    question: "How do I list my business on Mondoway?",
    answer:
      "Send us a message using the form on this page and choose “Listing my business” as your area of interest. Our team will walk you through verification and getting your products or services live."
  },
  {
    question: "How do I delete my account?",
    answer:
      "You can request deletion at any time from the account deletion pages linked in the footer — one for business accounts and one for personal accounts."
  }
];

/* ------------------------------------------------------------------ */
/*  Form field                                                          */
/* ------------------------------------------------------------------ */

function Field({ label, name, error, required, hint, children }) {
  const id = useId();
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;

  return (
    <div className="group/field">
      <label
        htmlFor={id}
        className="block text-sm font-semibold text-[#1E1B16] transition-colors duration-300 group-focus-within/field:text-[#6A0DAD]"
      >
        {label}
        {required && <span className="ml-1 text-[#6A0DAD]">*</span>}
      </label>

      {/* The wrapper carries the gradient underline that wipes in on focus;
          the control itself keeps the ring and the border colour. */}
      <div className="mw-field-wrap relative mt-2 overflow-hidden rounded-xl">
        {children({
          id,
          name,
          "aria-invalid": error ? "true" : undefined,
          "aria-describedby": describedBy,
          className: `mw-field w-full rounded-xl border bg-white px-4 py-3 text-[15px] text-[#1E1B16] placeholder:text-[#9A9085] ${
            error
              ? "border-[#D94444] focus:border-[#D94444]"
              : "border-[#EAE2D6] hover:border-[#DCCDF2]"
          }`
        })}
      </div>

      {error ? (
        <p
          id={`${id}-error`}
          className="mt-2 flex items-center gap-1.5 text-[13px] font-medium text-[#D94444]"
        >
          <Icon name="alert" className="h-4 w-4 shrink-0" />
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="mt-2 text-[13px] text-[#7A6F61]">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  FAQ accordion                                                       */
/* ------------------------------------------------------------------ */

function FaqItem({ faq, isOpen, onToggle }) {
  const id = useId();

  return (
    <div className="overflow-hidden rounded-2xl border border-[#EAE2D6] bg-white transition-colors duration-300 hover:border-[#D8B4FE]">
      <h3>
        <button
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-controls={id}
          className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left focus:outline-none focus-visible:ring-4 focus-visible:ring-[#6A0DAD]/20"
        >
          <span className="text-[17px] font-semibold text-[#1E1B16]">{faq.question}</span>
          <span
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#F4E8FF] text-[#6A0DAD] transition-transform duration-300 ${
              isOpen ? "rotate-180" : ""
            }`}
          >
            <Icon name="chevron" className="h-5 w-5" />
          </span>
        </button>
      </h3>
      {/* Animating grid-template-rows lets the panel expand to its natural
          height; `inert` keeps the collapsed copy out of the a11y tree. */}
      <div
        className={`grid transition-[grid-template-rows] duration-400 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div id={id} inert={!isOpen} className="overflow-hidden">
          <div className="border-t border-[#F0E9DE] px-6 py-5 text-[15px] leading-relaxed text-[#4D4B46]">
            {faq.answer}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                                */
/* ------------------------------------------------------------------ */

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  company: "",
  subject: "",
  message: "",
  service: ""
};

function ContactUs() {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  const validate = () => {
    const next = {};
    if (!formData.name.trim()) next.name = "Please tell us your name.";
    if (!formData.email.trim()) {
      next.email = "We need an email address to reply to.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      next.email = "That email address doesn't look right.";
    }
    if (!formData.subject.trim()) next.subject = "Please add a short subject.";
    if (!formData.message.trim()) {
      next.message = "Please write your message.";
    } else if (formData.message.trim().length < 20) {
      next.message = "A little more detail helps us respond properly.";
    }
    return next;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const found = validate();
    setErrors(found);

    const invalidNames = Object.keys(found);
    if (invalidNames.length > 0) {
      // Target by `name`, which is already in the DOM — querying for
      // [aria-invalid] here would run before React has re-rendered.
      const first = document.querySelector(`[name="${invalidNames[0]}"]`);
      if (first instanceof HTMLElement) {
        first.focus();
        first.scrollIntoView({ block: "center", behavior: "smooth" });
      }
      return;
    }

    // No backend exists yet, so hand the composed message to the visitor's
    // mail client rather than pretending it was delivered.
    const body = [
      `Name: ${formData.name}`,
      `Email: ${formData.email}`,
      formData.phone && `Phone: ${formData.phone}`,
      formData.company && `Company: ${formData.company}`,
      formData.service && `Interested in: ${formData.service}`,
      "",
      formData.message
    ]
      .filter(Boolean)
      .join("\n");

    window.location.href = `mailto:${EMAIL}?subject=${encodeURIComponent(
      formData.subject
    )}&body=${encodeURIComponent(body)}`;

    setSent(true);
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setErrors({});
    setSent(false);
  };

  return (
    <div className="bg-[#FAF8F2] text-[#1E1B16]">
      {/* ============================== HERO ============================== */}
      <section className="relative isolate overflow-hidden bg-[#220339]">
        <HeroBackdrop tone="hero" foot />

        <div className="relative mx-auto max-w-4xl px-6 pb-32 pt-32 text-center sm:pt-36 md:px-10 lg:pt-40">
          <Reveal>
            <span className="inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/10 px-4 py-1.5 text-[13px] font-semibold tracking-wide text-white backdrop-blur">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#D8B4FE] opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#D8B4FE]" />
              </span>
              We reply within 24 hours
            </span>
          </Reveal>

          <Reveal delay={90}>
            <h1 className="mw-display mt-8 text-white">
              Get in{" "}
              <span className="bg-gradient-to-r from-[#E9D5FF] via-[#D8B4FE] to-[#F4E8FF] bg-clip-text text-transparent">
                touch.
              </span>
            </h1>
          </Reveal>

          <Reveal delay={170}>
            <p className="mw-lede mx-auto mt-7 max-w-2xl text-[#DCC9F2]">
              Whether you have a question about the app, want to list your business, or
              just need a hand — we&rsquo;d love to hear from you.
            </p>
          </Reveal>

          <Reveal delay={250}>
            <div className="mt-10 flex flex-col justify-center gap-3.5 sm:flex-row">
              <Button
                variant="light"
                icon="mail"
                onClick={() => {
                  window.location.href = `mailto:${EMAIL}`;
                }}
              >
                Email us
              </Button>
              <Button
                variant="glass"
                icon="phone"
                onClick={() => {
                  window.location.href = `tel:${PHONE_DIAL}`;
                }}
              >
                Call us
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ========================= CONTACT METHODS ======================== */}
      <section className="py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <Reveal>
            <SectionHeading
              eyebrow="How to reach us"
              title="Pick whichever suits you."
              lede="Choose your preferred way to connect with us. We're here to help and respond quickly."
              align="center"
            />
          </Reveal>

          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {CONTACT_METHODS.map((method, index) => (
              <Reveal key={method.id} delay={index * 80} className="h-full">
                <a
                  href={method.action}
                  target={method.action.startsWith("http") ? "_blank" : undefined}
                  rel={method.action.startsWith("http") ? "noreferrer" : undefined}
                  className="group flex h-full flex-col rounded-2xl border border-[#EAE2D6] bg-white p-7 shadow-[0_1px_2px_rgba(30,27,22,0.04)] transition-all duration-300 hover:-translate-y-1.5 hover:border-[#D8B4FE] hover:shadow-[0_28px_50px_-28px_rgba(106,13,173,0.45)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#6A0DAD]/20"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#F4E8FF] to-[#EADCFB] text-[#6A0DAD] ring-1 ring-[#EBDBFA] transition-all duration-300 group-hover:from-[#6A0DAD] group-hover:to-[#4C087D] group-hover:text-white group-hover:ring-[#6A0DAD]">
                    <Icon name={method.icon} className="h-6 w-6" />
                  </span>

                  <h3 className="mt-6 text-xl font-semibold tracking-tight text-[#1E1B16]">
                    {method.title}
                  </h3>
                  <p className="mt-1 text-[13px] font-medium text-[#7A6F61]">
                    {method.subtitle}
                  </p>
                  <p className="mt-3 break-words font-semibold text-[#6A0DAD]">
                    {method.value}
                  </p>
                  <p className="mt-3 flex-1 text-[15px] leading-relaxed text-[#4D4B46]">
                    {method.description}
                  </p>

                  <span className="mt-5 inline-flex items-center gap-2 text-[15px] font-semibold text-[#6A0DAD]">
                    Open
                    <Icon
                      name="arrow"
                      className="h-[18px] w-[18px] transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ======================= FORM + OFFICE INFO ======================= */}
      <section className="relative border-y border-[#EDE4D8] bg-gradient-to-b from-white via-white to-[#FBF7FF] py-24 lg:py-32">
        <div className="mw-grid-light absolute inset-0" aria-hidden="true" />

        <div className="relative mx-auto max-w-7xl px-6 md:px-10">
          <div className="grid gap-10 lg:grid-cols-12 lg:gap-12">
            {/* Form */}
            <div className="lg:col-span-7">
              <Reveal>
                <SectionHeading
                  eyebrow="Send us a message"
                  title="Tell us what you need."
                  lede="Fill out the form below and we'll get back to you as soon as possible with a personalized response."
                />
              </Reveal>

              <Reveal delay={100}>
                <div className="mt-10 rounded-[1.5rem] border border-[#EAE2D6] bg-white p-7 shadow-[0_30px_60px_-40px_rgba(30,27,22,0.4)] sm:p-9">
                  {sent ? (
                    <div className="py-10 text-center">
                      <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-[#6A0DAD] to-[#4C087D] text-white shadow-xl shadow-[#6A0DAD]/30">
                        <Icon name="checkCircle" className="h-8 w-8" />
                      </span>
                      <h3 className="mt-7 text-2xl font-semibold tracking-tight text-[#1E1B16]">
                        Your message is ready to send
                      </h3>
                      <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-[#4D4B46]">
                        We&rsquo;ve opened your email app with the details filled in.
                        Send it and we&rsquo;ll reply within 24 hours — or write to us
                        directly at{" "}
                        <a
                          href={`mailto:${EMAIL}`}
                          className="font-semibold text-[#6A0DAD] hover:underline"
                        >
                          {EMAIL}
                        </a>
                        .
                      </p>
                      <Button onClick={resetForm} variant="outline" size="md" className="mt-8">
                        Write another message
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} noValidate className="space-y-6">
                      <div className="grid gap-6 sm:grid-cols-2">
                        <Field label="Full name" name="name" required error={errors.name}>
                          {(props) => (
                            <input
                              {...props}
                              type="text"
                              value={formData.name}
                              onChange={handleInputChange}
                              placeholder="Your full name"
                              autoComplete="name"
                            />
                          )}
                        </Field>

                        <Field label="Email address" name="email" required error={errors.email}>
                          {(props) => (
                            <input
                              {...props}
                              type="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              placeholder="your.email@example.com"
                              autoComplete="email"
                            />
                          )}
                        </Field>
                      </div>

                      <div className="grid gap-6 sm:grid-cols-2">
                        <Field label="Phone number" name="phone" error={errors.phone}>
                          {(props) => (
                            <input
                              {...props}
                              type="tel"
                              value={formData.phone}
                              onChange={handleInputChange}
                              placeholder={PHONE_DISPLAY}
                              autoComplete="tel"
                            />
                          )}
                        </Field>

                        <Field label="Company / organisation" name="company">
                          {(props) => (
                            <input
                              {...props}
                              type="text"
                              value={formData.company}
                              onChange={handleInputChange}
                              placeholder="Your company name"
                              autoComplete="organization"
                            />
                          )}
                        </Field>
                      </div>

                      <Field label="What is this about?" name="service">
                        {(props) => (
                          <select
                            {...props}
                            value={formData.service}
                            onChange={handleInputChange}
                          >
                            <option value="">Select an area…</option>
                            {SERVICE_OPTIONS.map((service) => (
                              <option key={service} value={service}>
                                {service}
                              </option>
                            ))}
                          </select>
                        )}
                      </Field>

                      <Field label="Subject" name="subject" required error={errors.subject}>
                        {(props) => (
                          <input
                            {...props}
                            type="text"
                            value={formData.subject}
                            onChange={handleInputChange}
                            placeholder="Brief description of your inquiry"
                          />
                        )}
                      </Field>

                      <Field
                        label="Message"
                        name="message"
                        required
                        error={errors.message}
                        hint="The more context you give us, the better we can help."
                      >
                        {(props) => (
                          <textarea
                            {...props}
                            rows={6}
                            value={formData.message}
                            onChange={handleInputChange}
                            placeholder="Tell us what you need, and any specific questions you have…"
                            className={`${props.className} resize-y`}
                          />
                        )}
                      </Field>

                      <div className="flex flex-col items-start gap-4 border-t border-[#F0E9DE] pt-6 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-[13px] text-[#7A6F61]">
                          We typically respond within 24 hours during business days.
                        </p>
                        <Button type="submit" icon="send" size="md">
                          Send message
                        </Button>
                      </div>
                    </form>
                  )}
                </div>
              </Reveal>
            </div>

            {/* Office info */}
            <div className="lg:col-span-5">
              <Reveal delay={150}>
                <div className="rounded-[1.5rem] border border-[#EAE2D6] bg-white p-7 shadow-[0_1px_2px_rgba(30,27,22,0.04)] sm:p-8">
                  <h3 className="text-xl font-semibold tracking-tight text-[#1E1B16]">
                    Office details
                  </h3>

                  <dl className="mt-6 space-y-6">
                    {OFFICE_DETAILS.map((detail) => (
                      <div key={detail.title} className="flex gap-4">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F4E8FF] text-[#6A0DAD]">
                          <Icon name={detail.icon} className="h-5 w-5" />
                        </span>
                        <div>
                          <dt className="text-sm font-semibold text-[#1E1B16]">
                            {detail.title}
                          </dt>
                          <dd className="mt-1 space-y-0.5 text-[15px] leading-relaxed text-[#4D4B46]">
                            {detail.lines.map((line) => (
                              <div key={line}>{line}</div>
                            ))}
                          </dd>
                        </div>
                      </div>
                    ))}
                  </dl>

                  <a
                    href={MAPS_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="group mt-8 flex items-center justify-between gap-4 rounded-2xl border border-[#EADCFB] bg-[#FBF7FF] px-5 py-4 transition-all duration-300 hover:border-[#D8B4FE] hover:bg-[#F4E8FF] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#6A0DAD]/20"
                  >
                    <span className="flex items-center gap-3">
                      <Icon name="map" className="h-5 w-5 text-[#6A0DAD]" />
                      <span className="text-[15px] font-semibold text-[#4C087D]">
                        View on Google Maps
                      </span>
                    </span>
                    <Icon
                      name="arrow"
                      className="h-[18px] w-[18px] text-[#6A0DAD] transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </a>
                </div>
              </Reveal>

              <Reveal delay={220}>
                <div className="mt-6 rounded-[1.5rem] border border-[#EAE2D6] bg-white p-7 shadow-[0_1px_2px_rgba(30,27,22,0.04)] sm:p-8">
                  <h3 className="text-xl font-semibold tracking-tight text-[#1E1B16]">
                    Follow us
                  </h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-[#4D4B46]">
                    Stay updated with our latest projects and insights.
                  </p>

                  <div className="mt-6 flex flex-wrap gap-2.5">
                    {SOCIAL_LINKS.map((social) => (
                      <a
                        key={social.name}
                        href={social.url}
                        aria-label={social.name}
                        title={social.name}
                        className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#EAE2D6] bg-white text-[#4D4B46] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#D8B4FE] hover:bg-[#6A0DAD] hover:text-white focus:outline-none focus-visible:ring-4 focus-visible:ring-[#6A0DAD]/20"
                      >
                        <Icon
                          name={social.icon}
                          filled={["facebook", "linkedin", "x"].includes(social.icon)}
                          className="h-5 w-5"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* =============================== FAQ ============================== */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-3xl px-6 md:px-10">
          <Reveal>
            <SectionHeading
              eyebrow="FAQ"
              title="Frequently asked questions."
              lede="Quick answers to the things people ask us most."
              align="center"
            />
          </Reveal>

          <div className="mt-14 space-y-3">
            {FAQS.map((faq, index) => (
              <Reveal key={faq.question} delay={index * 70}>
                <FaqItem
                  faq={faq}
                  isOpen={openFaq === index}
                  onToggle={() => setOpenFaq(openFaq === index ? -1 : index)}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* =============================== CTA ============================== */}
      <section className="relative isolate overflow-hidden bg-[#220339]">
        <HeroBackdrop tone="cta" />

        <div className="relative mx-auto max-w-4xl px-6 py-28 text-center md:px-10 lg:py-32">
          <Reveal>
            <h2 className="mw-h2 text-white">Still have a question?</h2>
            <p className="mw-lede mx-auto mt-6 max-w-2xl text-[#DCC9F2]">
              Call us directly and we&rsquo;ll help you straight away.
            </p>
          </Reveal>

          <Reveal delay={120}>
            <div className="mt-11 flex flex-col justify-center gap-3.5 sm:flex-row">
              <Button
                variant="light"
                icon="phone"
                onClick={() => {
                  window.location.href = `tel:${PHONE_DIAL}`;
                }}
              >
                Call {PHONE_DISPLAY}
              </Button>
              <Button
                variant="glass"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              >
                Back to top
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

export default ContactUs;
